import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, AlertTriangle, DollarSign, ShoppingCart, 
  TrendingUp, Edit2, Save, X, LogOut, Trash2, Plus,
  BarChart3, PackageSearch, ArrowLeft, ScanLine, Store,
  Globe, Smartphone, Download, FileSpreadsheet, FileText,
  TrendingDown, Lightbulb, Calendar, Filter, ClipboardList,
  Image as ImageIcon, Search
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { POSScanner } from '../components/POSScanner';
import { OrdersModule } from '../components/OrdersModule';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_NAMES = {
  'cervejas': 'Cervejas',
  'cachacas': 'Cachaças',
  'refrigerantes': 'Refrigerantes',
  'mercado': 'Mercado',
  'agua-gelo': 'Água & Gelo'
};

const CATEGORIES = [
  { id: 'cervejas', name: 'Cervejas' },
  { id: 'cachacas', name: 'Cachaças & Destilados' },
  { id: 'refrigerantes', name: 'Refrigerantes' },
  { id: 'mercado', name: 'Mini Mercado' },
  { id: 'agua-gelo', name: 'Água & Gelo' },
];

const COLORS = ['#004AAD', '#FFD700', '#00A859', '#FF6B6B', '#4ECDC4'];

export const Admin = () => {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [salesPeriod, setSalesPeriod] = useState('all'); // 'today', 'week', 'month', 'all'
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(null);
  const [inventoryFilter, setInventoryFilter] = useState('all'); // all, low_stock, out_of_stock
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    cost: '',
    category: 'cervejas',
    description: '',
    image: '',
    unit: 'un',
    stock: '',
    barcode: '',
    discount: 0
  });
  const [editProduct, setEditProduct] = useState({
    name: '',
    price: '',
    cost: '',
    category: 'cervejas',
    description: '',
    image: '',
    unit: 'un',
    stock: '',
    barcode: '',
    discount: 0
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [productsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/dashboard?period=${salesPeriod}`, { headers })
      ]);
      setProducts(productsRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Refetch when period changes
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [salesPeriod]);

  const handleEdit = (product) => {
    setEditProduct({
      id: product.id,
      name: product.name || '',
      price: product.price || 0,
      cost: product.cost || 0,
      category: product.category || 'cervejas',
      description: product.description || '',
      image: product.image || '',
      unit: product.unit || 'un',
      stock: product.stock || 0,
      barcode: product.barcode || '',
      discount: product.discount || 0
    });
    setShowEditModal(product);
  };

  const handleSaveEdit = async () => {
    if (!editProduct.name || editProduct.price === '' || editProduct.stock === '') {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updateData = {
        name: editProduct.name,
        price: parseFloat(editProduct.price),
        cost: parseFloat(editProduct.cost) || 0,
        stock: parseInt(editProduct.stock),
        description: editProduct.description,
        image: editProduct.image || 'https://images.unsplash.com/photo-1608270586620-248524c67de9?w=300',
        barcode: editProduct.barcode,
        discount: parseFloat(editProduct.discount) || 0,
        category: editProduct.category,
        unit: editProduct.unit
      };
      
      await axios.patch(`${API}/admin/products/${editProduct.id}`, updateData, { headers });
      toast.success('Produto atualizado com sucesso!');
      setShowEditModal(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar produto');
    }
  };

  const handleQuickEdit = (product) => {
    setEditingProduct(product.id);
    setEditValues({
      price: product.price,
      stock: product.stock
    });
  };

  const handleSaveQuick = async (productId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/products/${productId}`, editValues, { headers });
      toast.success('Produto atualizado!');
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar produto');
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setEditValues({});
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price || !newProduct.stock) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        cost: parseFloat(newProduct.cost) || 0,
        stock: parseInt(newProduct.stock),
        image: newProduct.image || 'https://images.unsplash.com/photo-1608270586620-248524c67de9?w=300',
        barcode: newProduct.barcode || '',
        discount: parseFloat(newProduct.discount) || 0
      };
      
      await axios.post(`${API}/admin/products`, productData, { headers });
      toast.success('Produto adicionado com sucesso!');
      setShowAddModal(false);
      setNewProduct({
        name: '',
        price: '',
        cost: '',
        category: 'cervejas',
        description: '',
        image: '',
        unit: 'un',
        stock: '',
        barcode: '',
        discount: 0
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao adicionar produto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      toast.success('Produto removido com sucesso!');
      setShowDeleteDialog(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover produto');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.info('Logout realizado');
  };

  // Função para exportar inventário em CSV/Excel
  const exportInventoryToExcel = () => {
    if (!products.length) {
      toast.error('Nenhum produto para exportar');
      return;
    }

    const headers = ['ID', 'Nome', 'Categoria', 'Preço (R$)', 'Desconto (%)', 'Preço Final (R$)', 'Estoque', 'Código de Barras', 'Status'];
    const rows = products.map(p => {
      const finalPrice = p.discount > 0 ? (p.price * (1 - p.discount / 100)).toFixed(2) : p.price.toFixed(2);
      const status = p.stock === 0 ? 'Sem Estoque' : p.stock <= 10 ? 'Estoque Baixo' : 'Disponível';
      return [
        p.id,
        p.name,
        CATEGORY_NAMES[p.category] || p.category,
        p.price.toFixed(2),
        p.discount || 0,
        finalPrice,
        p.stock,
        p.barcode || '-',
        status
      ];
    });

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventario_armazem88_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Relatório Excel exportado com sucesso!');
  };

  // Função para exportar inventário em PDF (usando print)
  const exportInventoryToPDF = () => {
    if (!products.length) {
      toast.error('Nenhum produto para exportar');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Inventário - Armazém 88</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #004AAD; text-align: center; }
          h2 { color: #333; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #004AAD; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .sem-estoque { color: red; font-weight: bold; }
          .estoque-baixo { color: orange; font-weight: bold; }
          .disponivel { color: green; }
          .discount { background-color: #ffe0e0; }
          .summary { margin: 20px 0; padding: 15px; background: #f0f0f0; border-radius: 8px; }
          .date { text-align: right; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>📦 Relatório de Inventário - Armazém 88</h1>
        <p class="date">Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
        
        <div class="summary">
          <strong>Resumo:</strong><br>
          Total de Produtos: ${products.length}<br>
          Produtos em Estoque: ${products.filter(p => p.stock > 10).length}<br>
          Estoque Baixo (≤10): ${products.filter(p => p.stock > 0 && p.stock <= 10).length}<br>
          Sem Estoque: ${products.filter(p => p.stock === 0).length}<br>
          Produtos com Desconto: ${products.filter(p => p.discount > 0).length}<br>
          Valor Total do Estoque: R$ ${products.reduce((acc, p) => acc + (p.price * p.stock), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </div>

        <table>
          <thead>
            <tr>
              <th>Produto</th>
              <th>Categoria</th>
              <th>Preço</th>
              <th>Desconto</th>
              <th>Preço Final</th>
              <th>Estoque</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${products.map(p => {
              const finalPrice = p.discount > 0 ? (p.price * (1 - p.discount / 100)) : p.price;
              const statusClass = p.stock === 0 ? 'sem-estoque' : p.stock <= 10 ? 'estoque-baixo' : 'disponivel';
              const status = p.stock === 0 ? 'SEM ESTOQUE' : p.stock <= 10 ? 'ESTOQUE BAIXO' : 'Disponível';
              const rowClass = p.discount > 0 ? 'discount' : '';
              return `
                <tr class="${rowClass}">
                  <td>${p.name}</td>
                  <td>${CATEGORY_NAMES[p.category] || p.category}</td>
                  <td>R$ ${p.price.toFixed(2)}</td>
                  <td>${p.discount > 0 ? p.discount + '%' : '-'}</td>
                  <td>R$ ${finalPrice.toFixed(2)}</td>
                  <td>${p.stock}</td>
                  <td class="${statusClass}">${status}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Relatório PDF gerado para impressão!');
  };

  // Dados para gráficos de vendas
  const webSalesData = dashboard?.web_orders ? [
    { name: 'PIX', value: dashboard.web_orders.by_method?.pix || 0 },
    { name: 'Entrega', value: dashboard.web_orders.by_method?.entrega || 0 }
  ] : [];

  const pdvSalesData = dashboard?.pdv_sales ? [
    { name: 'Dinheiro', value: dashboard.pdv_sales.by_method?.dinheiro || 0 },
    { name: 'Cartão', value: dashboard.pdv_sales.by_method?.cartao || 0 },
    { name: 'PIX', value: dashboard.pdv_sales.by_method?.pix || 0 }
  ] : [];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stockChartData = dashboard ? Object.entries(dashboard.stock_by_category).map(([key, value]) => ({
    name: CATEGORY_NAMES[key] || key,
    estoque: value.total,
    produtos: value.items
  })) : [];

  const pieData = dashboard ? [
    { name: 'Em Estoque', value: dashboard.products.in_stock },
    { name: 'Estoque Baixo', value: dashboard.products.low_stock },
    { name: 'Sem Estoque', value: dashboard.products.out_of_stock }
  ] : [];

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-page">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-40">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-white/80 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-black">Painel Administrativo</h1>
            </div>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex gap-2 md:gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-3 md:px-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'dashboard' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-dashboard"
            >
              <BarChart3 className="w-5 h-5 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-3 md:px-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'orders' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-orders"
            >
              <ClipboardList className="w-5 h-5 inline mr-2" />
              Pedidos
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`py-4 px-3 md:px-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'inventory' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-inventory"
            >
              <Package className="w-5 h-5 inline mr-2" />
              Inventário
            </button>
            <button
              onClick={() => setActiveTab('pos')}
              className={`py-4 px-3 md:px-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'pos' 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-pos"
            >
              <Store className="w-5 h-5 inline mr-2" />
              PDV
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-8">
            {/* Period Filter */}
            <div className="bg-card rounded-xl border p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-primary" />
                  <span className="font-bold">Filtrar por Período:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant={salesPeriod === 'today' ? 'default' : 'outline'}
                    onClick={() => setSalesPeriod('today')}
                    className={salesPeriod === 'today' ? 'bg-primary' : ''}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Hoje
                  </Button>
                  <Button 
                    size="sm" 
                    variant={salesPeriod === 'week' ? 'default' : 'outline'}
                    onClick={() => setSalesPeriod('week')}
                    className={salesPeriod === 'week' ? 'bg-primary' : ''}
                  >
                    Últimos 7 dias
                  </Button>
                  <Button 
                    size="sm" 
                    variant={salesPeriod === 'month' ? 'default' : 'outline'}
                    onClick={() => setSalesPeriod('month')}
                    className={salesPeriod === 'month' ? 'bg-primary' : ''}
                  >
                    Últimos 30 dias
                  </Button>
                  <Button 
                    size="sm" 
                    variant={salesPeriod === 'all' ? 'default' : 'outline'}
                    onClick={() => setSalesPeriod('all')}
                    className={salesPeriod === 'all' ? 'bg-primary' : ''}
                  >
                    Todo Período
                  </Button>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-3 rounded-xl">
                    <Package className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground">{dashboard.products.total}</p>
                    <p className="text-sm text-muted-foreground">Produtos</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-100 p-3 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground">{dashboard.products.low_stock}</p>
                    <p className="text-sm text-muted-foreground">Estoque Baixo</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground">
                      R$ {(dashboard.combined?.total_revenue || 0).toFixed(0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Receita Total</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-xl">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground">{(dashboard.combined?.total_transactions || 0)}</p>
                    <p className="text-sm text-muted-foreground">Vendas Totais</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Reports - Web and PDV */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Web Sales Report */}
              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-500" />
                    Vendas Web (Online)
                  </h3>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                    {dashboard.web_orders?.total || 0} pedidos
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Receita Web</p>
                    <p className="text-2xl font-black text-blue-600">
                      R$ {(dashboard.web_orders?.revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Hoje</p>
                    <p className="text-2xl font-black text-green-600">
                      {dashboard.web_orders?.today_count || 0} vendas
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Pagamentos:</p>
                  <div className="flex gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                      PIX: R$ {(dashboard.web_orders?.by_method?.pix || 0).toFixed(2)}
                    </span>
                    <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">
                      Entrega: R$ {(dashboard.web_orders?.by_method?.entrega || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* PDV Sales Report */}
              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Store className="w-5 h-5 text-green-500" />
                    Vendas PDV (Loja Física)
                  </h3>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold">
                    {dashboard.pdv_sales?.total || 0} vendas
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Receita PDV</p>
                    <p className="text-2xl font-black text-green-600">
                      R$ {(dashboard.pdv_sales?.revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Hoje</p>
                    <p className="text-2xl font-black text-blue-600">
                      {dashboard.pdv_sales?.today_count || 0} vendas
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Pagamentos:</p>
                  <div className="flex gap-2 flex-wrap">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                      Dinheiro: R$ {(dashboard.pdv_sales?.by_method?.dinheiro || 0).toFixed(2)}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                      Cartão: R$ {(dashboard.pdv_sales?.by_method?.cartao || 0).toFixed(2)}
                    </span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                      PIX: R$ {(dashboard.pdv_sales?.by_method?.pix || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Analysis and Insights */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Análise e Sugestões para Melhorar Vendas
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboard.products.out_of_stock > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <TrendingDown className="w-4 h-4" />
                      <span className="font-bold text-sm">Atenção</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <strong>{dashboard.products.out_of_stock} produtos</strong> sem estoque. 
                      Reponha para não perder vendas!
                    </p>
                  </div>
                )}
                {dashboard.products.low_stock > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 text-yellow-600 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-bold text-sm">Estoque Baixo</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <strong>{dashboard.products.low_stock} produtos</strong> com estoque baixo. 
                      Faça pedido ao fornecedor.
                    </p>
                  </div>
                )}
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 text-green-600 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-bold text-sm">Dica</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {(dashboard.pdv_sales?.revenue || 0) > (dashboard.web_orders?.revenue || 0) 
                      ? 'PDV lidera! Invista em promoções online para equilibrar.'
                      : 'Web vendendo bem! Mantenha o marketing digital ativo.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Stock by Category */}
              <div className="bg-card rounded-xl border p-6">
                <h3 className="font-bold text-lg mb-4">Estoque por Categoria</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stockChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="estoque" fill="#004AAD" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Stock Status Pie */}
              <div className="bg-card rounded-xl border p-6">
                <h3 className="font-bold text-lg mb-4">Status do Estoque</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Comparative Chart - Web vs PDV */}
            <div className="bg-card rounded-xl border p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Comparativo Web vs PDV
              </h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    {
                      name: 'Vendas',
                      Web: dashboard.web_orders?.total || 0,
                      PDV: dashboard.pdv_sales?.total || 0,
                    },
                    {
                      name: 'Receita (R$)',
                      Web: dashboard.web_orders?.revenue || 0,
                      PDV: dashboard.pdv_sales?.revenue || 0,
                    },
                    {
                      name: 'Hoje',
                      Web: dashboard.web_orders?.today_count || 0,
                      PDV: dashboard.pdv_sales?.today_count || 0,
                    }
                  ]}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'Web' || name === 'PDV' 
                        ? (typeof value === 'number' && value > 100 ? `R$ ${value.toFixed(2)}` : value)
                        : value,
                      name
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="Web" fill="#3B82F6" name="🌐 Web" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="PDV" fill="#22C55E" name="🏪 PDV" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Vendas Web</p>
                  <p className="text-xl font-black text-blue-600">{dashboard.web_orders?.total || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Vendas PDV</p>
                  <p className="text-xl font-black text-green-600">{dashboard.pdv_sales?.total || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Receita Web</p>
                  <p className="text-xl font-black text-blue-600">R$ {(dashboard.web_orders?.revenue || 0).toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Receita PDV</p>
                  <p className="text-xl font-black text-green-600">R$ {(dashboard.pdv_sales?.revenue || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Low Stock Alert */}
            {dashboard.low_stock_products.length > 0 && (
              <div className="bg-card rounded-xl border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    Produtos com Estoque Baixo - Reabastecer!
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setInventoryFilter('low_stock');
                      setActiveTab('inventory');
                    }}
                    className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                  >
                    Ver todos no Inventário
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboard.low_stock_products.slice(0, 6).map(product => (
                    <div 
                      key={product.id} 
                      className="flex items-center gap-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                      onClick={() => {
                        setInventoryFilter('low_stock');
                        setActiveTab('inventory');
                      }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className={`text-sm font-bold ${product.stock === 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                          {product.stock === 0 ? 'SEM ESTOQUE' : `${product.stock} unidades`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {dashboard.low_stock_products.length > 6 && (
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    +{dashboard.low_stock_products.length - 6} produtos com estoque baixo
                  </p>
                )}
              </div>
            )}

            {/* Inventory Value */}
            <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80">Valor Total do Estoque</p>
                  <p className="text-4xl font-black mt-1">
                    R$ {dashboard.inventory_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <TrendingUp className="w-16 h-16 text-white/30" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-6">
            {/* Add Product Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Gerenciar Produtos</h2>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-product-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adicionar Produto
              </Button>
            </div>

            {/* Products Table */}
            <div className="bg-card rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-medium">Produto</th>
                      <th className="text-left p-4 font-medium">Categoria</th>
                      <th className="text-right p-4 font-medium">Preço</th>
                      <th className="text-right p-4 font-medium">Desconto</th>
                      <th className="text-right p-4 font-medium">Estoque</th>
                      <th className="text-center p-4 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr key={product.id} className="border-t hover:bg-muted/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium">{product.name}</p>
                              <p className="text-sm text-muted-foreground">{product.description}</p>
                              {product.barcode && (
                                <p className="text-xs text-muted-foreground">Cód: {product.barcode}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground">
                          {CATEGORY_NAMES[product.category] || product.category}
                        </td>
                        <td className="p-4 text-right">
                          {editingProduct === product.id ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={editValues.price}
                              onChange={(e) => setEditValues(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                              className="w-24 text-right"
                              data-testid={`edit-price-${product.id}`}
                            />
                          ) : (
                            <div>
                              {product.discount > 0 ? (
                                <>
                                  <span className="text-sm text-muted-foreground line-through">
                                    R$ {product.price.toFixed(2)}
                                  </span>
                                  <br />
                                  <span className="font-bold text-green-600">
                                    R$ {(product.price * (1 - product.discount / 100)).toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <span className="font-bold text-primary">
                                  R$ {product.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {product.discount > 0 ? (
                            <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-sm font-bold">
                              -{product.discount}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {editingProduct === product.id ? (
                            <Input
                              type="number"
                              value={editValues.stock}
                              onChange={(e) => setEditValues(prev => ({ ...prev, stock: parseInt(e.target.value) }))}
                              className="w-20 text-right"
                              data-testid={`edit-stock-${product.id}`}
                            />
                          ) : (
                            <span className={`font-bold ${
                              product.stock === 0 ? 'text-red-600' : 
                              product.stock <= 10 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {product.stock}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {editingProduct === product.id ? (
                            <div className="flex justify-center gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleSaveQuick(product.id)}
                                data-testid={`save-${product.id}`}
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={handleCancel}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleEdit(product)}
                                title="Editar tudo"
                                data-testid={`edit-${product.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setShowDeleteDialog(product)}
                                title="Apagar"
                                data-testid={`delete-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders Module */}
        {activeTab === 'orders' && (
          <OrdersModule token={token} />
        )}

        {activeTab === 'inventory' && (
          <div className="space-y-6">
            {/* Header with actions */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">Gestão de Inventário</h2>
                <p className="text-muted-foreground">
                  Gerencie produtos, estoque, preços e custos
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setShowAddModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="add-product-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Novo Produto
                </Button>
                <Button onClick={exportInventoryToExcel} variant="outline">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar Excel
                </Button>
                <Button onClick={exportInventoryToPDF} variant="outline">
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm"
                variant={inventoryFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setInventoryFilter('all')}
              >
                Todos ({products.length})
              </Button>
              <Button 
                size="sm"
                variant={inventoryFilter === 'low_stock' ? 'default' : 'outline'}
                onClick={() => setInventoryFilter('low_stock')}
                className={inventoryFilter !== 'low_stock' ? 'text-yellow-600 border-yellow-300' : ''}
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Estoque Baixo ({products.filter(p => p.stock > 0 && p.stock <= 10).length})
              </Button>
              <Button 
                size="sm"
                variant={inventoryFilter === 'out_of_stock' ? 'default' : 'outline'}
                onClick={() => setInventoryFilter('out_of_stock')}
                className={inventoryFilter !== 'out_of_stock' ? 'text-red-600 border-red-300' : ''}
              >
                Sem Estoque ({products.filter(p => p.stock === 0).length})
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-600">Disponíveis</p>
                <p className="text-2xl font-black text-green-700">
                  {products.filter(p => p.stock > 10).length}
                </p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm text-yellow-600">Estoque Baixo</p>
                <p className="text-2xl font-black text-yellow-700">
                  {products.filter(p => p.stock > 0 && p.stock <= 10).length}
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-600">Sem Estoque</p>
                <p className="text-2xl font-black text-red-700">
                  {products.filter(p => p.stock === 0).length}
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-600">Valor Total</p>
                <p className="text-2xl font-black text-purple-700">
                  R$ {products.reduce((acc, p) => acc + (p.price * p.stock), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products
                .filter(p => {
                  if (inventoryFilter === 'low_stock') return p.stock > 0 && p.stock <= 10;
                  if (inventoryFilter === 'out_of_stock') return p.stock === 0;
                  return true;
                })
                .map(product => (
                <div key={product.id} className={`bg-card rounded-xl border overflow-hidden ${product.stock === 0 ? 'opacity-75 border-red-200' : product.stock <= 10 ? 'border-yellow-200' : ''}`}>
                  {/* Product Image */}
                  <div className="relative aspect-video bg-muted">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    {product.stock === 0 && (
                      <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">SEM ESTOQUE</span>
                      </div>
                    )}
                    {product.stock > 0 && product.stock <= 10 && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        Baixo: {product.stock}
                      </div>
                    )}
                    {product.discount > 0 && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        -{product.discount}%
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-bold text-lg line-clamp-1">{product.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">{product.description}</p>
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">{CATEGORY_NAMES[product.category]}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-muted/50 rounded p-2">
                        <p className="text-xs text-muted-foreground">Custo</p>
                        <p className="font-bold">R$ {(product.cost || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-muted/50 rounded p-2">
                        <p className="text-xs text-muted-foreground">Preço</p>
                        <p className="font-bold text-green-600">R$ {product.price.toFixed(2)}</p>
                      </div>
                      <div className="bg-muted/50 rounded p-2">
                        <p className="text-xs text-muted-foreground">Estoque</p>
                        <p className={`font-bold ${product.stock === 0 ? 'text-red-600' : product.stock <= 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {product.stock} {product.unit}
                        </p>
                      </div>
                      <div className="bg-muted/50 rounded p-2">
                        <p className="text-xs text-muted-foreground">Desconto</p>
                        <p className="font-bold">{product.discount > 0 ? `${product.discount}%` : '-'}</p>
                      </div>
                    </div>

                    {product.barcode && (
                      <p className="text-xs text-muted-foreground">Cód: {product.barcode}</p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(product)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => setShowDeleteDialog(product)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'pos' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Ponto de Venda (PDV)</h2>
            <p className="text-muted-foreground">
              Escaneie os produtos para realizar vendas rápidas.
            </p>
            <POSScanner token={token} onSaleComplete={fetchData} products={products} />
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Produto</DialogTitle>
            <DialogDescription>
              Preencha os dados do novo produto
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Nome do Produto *</Label>
              <Input
                id="name"
                value={newProduct.name}
                onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Cerveja Brahma 600ml"
                className="mt-1"
                data-testid="new-product-name"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cost">Custo (R$)</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  value={newProduct.cost}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="new-product-cost"
                />
              </div>
              <div>
                <Label htmlFor="price">Preço (R$) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="new-product-price"
                />
              </div>
              <div>
                <Label htmlFor="stock">Estoque *</Label>
                <Input
                  id="stock"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, stock: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                  data-testid="new-product-stock"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="discount">Desconto (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={newProduct.discount}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, discount: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                  data-testid="new-product-discount"
                />
              </div>
              <div>
                <Label htmlFor="barcode">Código de Barras</Label>
                <Input
                  id="barcode"
                  value={newProduct.barcode}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder="Ex: 7891000315507"
                  className="mt-1"
                  data-testid="new-product-barcode"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select
                value={newProduct.category}
                onValueChange={(value) => setNewProduct(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="new-product-category">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={newProduct.description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição breve do produto"
                className="mt-1"
                data-testid="new-product-description"
              />
            </div>

            <div>
              <Label htmlFor="image">URL da Imagem</Label>
              <Input
                id="image"
                value={newProduct.image}
                onChange={(e) => setNewProduct(prev => ({ ...prev, image: e.target.value }))}
                placeholder="https://..."
                className="mt-1"
                data-testid="new-product-image"
              />
            </div>

            <div>
              <Label htmlFor="unit">Unidade</Label>
              <Select
                value={newProduct.unit}
                onValueChange={(value) => setNewProduct(prev => ({ ...prev, unit: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="un">Unidade (un)</SelectItem>
                  <SelectItem value="pct">Pacote (pct)</SelectItem>
                  <SelectItem value="cx">Caixa (cx)</SelectItem>
                  <SelectItem value="kg">Quilo (kg)</SelectItem>
                  <SelectItem value="l">Litro (l)</SelectItem>
                  <SelectItem value="saco">Saco</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddProduct} data-testid="save-new-product">
              Adicionar Produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={!!showEditModal} onOpenChange={() => setShowEditModal(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Produto</DialogTitle>
            <DialogDescription>
              Atualize os dados do produto
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Nome do Produto *</Label>
              <Input
                id="edit-name"
                value={editProduct.name}
                onChange={(e) => setEditProduct(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Cerveja Brahma 600ml"
                className="mt-1"
                data-testid="edit-product-name"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-cost">Custo (R$)</Label>
                <Input
                  id="edit-cost"
                  type="number"
                  step="0.01"
                  value={editProduct.cost}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="edit-product-cost"
                />
              </div>
              <div>
                <Label htmlFor="edit-price">Preço (R$) *</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={editProduct.price}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="edit-product-price"
                />
              </div>
              <div>
                <Label htmlFor="edit-stock">Estoque *</Label>
                <Input
                  id="edit-stock"
                  type="number"
                  value={editProduct.stock}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, stock: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                  data-testid="edit-product-stock"
                />
              </div>
            </div>

            {/* Profit margin */}
            {editProduct.cost > 0 && editProduct.price > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">
                  Margem de lucro: <strong>{(((editProduct.price - editProduct.cost) / editProduct.cost) * 100).toFixed(1)}%</strong>
                  {' '}(R$ {(editProduct.price - editProduct.cost).toFixed(2)} por unidade)
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-discount">Desconto (%)</Label>
                <Input
                  id="edit-discount"
                  type="number"
                  min="0"
                  max="100"
                  value={editProduct.discount}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, discount: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                  data-testid="edit-product-discount"
                />
                {editProduct.discount > 0 && editProduct.price > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    Preço com desconto: R$ {(editProduct.price * (1 - editProduct.discount / 100)).toFixed(2)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="edit-barcode">Código de Barras</Label>
                <Input
                  id="edit-barcode"
                  value={editProduct.barcode}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder="Ex: 7891000315507"
                  className="mt-1"
                  data-testid="edit-product-barcode"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-category">Categoria</Label>
              <Select
                value={editProduct.category}
                onValueChange={(value) => setEditProduct(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="edit-product-category">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-description">Descrição</Label>
              <Input
                id="edit-description"
                value={editProduct.description}
                onChange={(e) => setEditProduct(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição breve do produto"
                className="mt-1"
                data-testid="edit-product-description"
              />
            </div>

            <div>
              <Label htmlFor="edit-image">URL da Imagem</Label>
              <Input
                id="edit-image"
                value={editProduct.image}
                onChange={(e) => setEditProduct(prev => ({ ...prev, image: e.target.value }))}
                placeholder="https://..."
                className="mt-1"
                data-testid="edit-product-image"
              />
              {editProduct.image && (
                <div className="mt-2">
                  <img 
                    src={editProduct.image} 
                    alt="Preview" 
                    className="w-20 h-20 rounded-lg object-cover border"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="edit-unit">Unidade</Label>
              <Select
                value={editProduct.unit}
                onValueChange={(value) => setEditProduct(prev => ({ ...prev, unit: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="un">Unidade (un)</SelectItem>
                  <SelectItem value="pct">Pacote (pct)</SelectItem>
                  <SelectItem value="cx">Caixa (cx)</SelectItem>
                  <SelectItem value="kg">Quilo (kg)</SelectItem>
                  <SelectItem value="l">Litro (l)</SelectItem>
                  <SelectItem value="saco">Saco</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} data-testid="save-edit-product">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!showDeleteDialog} onOpenChange={() => setShowDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Produto</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{showDeleteDialog?.name}</strong> do estoque? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteProduct(showDeleteDialog?.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
